import { getMe, logout } from "api/auth";
import { ApiError } from "api/client";
import type { AccountDto, AuthResponse } from "api/types";
import {
	clearStoredSession,
	getRefreshLeadTimeMs,
	getValidStoredSession,
	hydrateStoredSession,
	readStoredSession,
	refreshStoredSession,
	storeAuthSession,
	type StoredSession,
	writeStoredSession
} from "auth";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getUtcTimestamp } from "utilities";

type SessionContextValue = {
	hasSession: boolean;
	isReady: boolean;
	user: AccountDto | null;
	signIn: (auth: AuthResponse) => void;
	signOut: () => void;
	setUser: (user: AccountDto) => void;
	refreshUser: () => Promise<void>;
	getAccessToken: () => Promise<string>;
	/** True right after a sign-in that reactivated a deactivated account (trees restored). */
	reactivationNoticeVisible: boolean;
	dismissReactivationNotice: () => void;
};

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

type SessionProviderProps = React.PropsWithChildren;

export const SessionProvider = ({ children }: SessionProviderProps) => {
	const [session, setSession] = useState<StoredSession | null>(() => readStoredSession());
	const [isReady, setIsReady] = useState(false);
	const [reactivationNoticeVisible, setReactivationNoticeVisible] = useState(false);

	const signIn = useCallback((auth: AuthResponse): void => {
		setSession(storeAuthSession(auth));
		setIsReady(true);

		// Signing into a deactivated account reactivates it server-side; surface a welcome-back notice.
		if (auth.wasReactivated) {
			setReactivationNoticeVisible(true);
		}
	}, []);

	const dismissReactivationNotice = useCallback((): void => setReactivationNoticeVisible(false), []);

	const signOut = useCallback((): void => {
		// Best-effort server revocation + httpOnly cookie clear; local state is dropped regardless of the result.
		logout().catch(() => undefined);
		clearStoredSession();
		setSession(null);
		setIsReady(true);
	}, []);

	const setUser = useCallback((user: AccountDto): void => {
		setSession((current: StoredSession | null) => {
			if (!current) {
				return current;
			}

			const next = { ...current, user };
			writeStoredSession(next);

			return next;
		});
	}, []);

	const syncSession = useCallback(async (): Promise<StoredSession | null> => {
		const next = await getValidStoredSession();
		setSession(next);

		return next;
	}, []);

	const getAccessToken = useCallback(async (): Promise<string> => {
		const next = await syncSession();

		return next?.accessToken ?? "";
	}, [syncSession]);

	const refreshUser = useCallback(async (): Promise<void> => {
		const token = await getAccessToken();
		if (!token) {
			return;
		}

		try {
			const user = await getMe(token);
			setUser(user);
		} catch (error) {
			if (error instanceof ApiError && error.status === 401) {
				signOut();
			}
		}
	}, [getAccessToken, setUser, signOut]);

	useEffect(() => {
		let isCurrent = true;

		hydrateStoredSession()
			.then((next: StoredSession | null) => {
				if (!isCurrent) {
					return;
				}

				setSession(next);
				setIsReady(true);
			})
			.catch(() => {
				if (!isCurrent) {
					return;
				}

				signOut();
			});

		return () => {
			isCurrent = false;
		};
	}, [signOut]);

	useEffect(() => {
		if (!session?.expiresAt) {
			return undefined;
		}

		const refreshAt = getUtcTimestamp(session.expiresAt) - Date.now() - getRefreshLeadTimeMs();
		const timer = window.setTimeout(() => {
			refreshStoredSession()
				.then((next: StoredSession | null) => {
					setSession(next);
					if (!next) {
						setIsReady(true);
					}
				})
				.catch(() => {
					signOut();
				});
		}, Math.max(refreshAt, 0));

		return () => {
			window.clearTimeout(timer);
		};
	}, [session?.accessToken, session?.expiresAt, signOut]);

	useEffect(() => {
		// A page restored from the back/forward cache carries its frozen in-memory session — including
		// one signed out after the snapshot was taken. Unlike the visibility sync below (which trusts an
		// unexpired in-memory token), force a server round-trip: the refresh cookie is the source of
		// truth, and a revoked/cleared cookie drops the restored page to anonymous immediately.
		const revalidateAfterRestore = (event: PageTransitionEvent): void => {
			if (!event.persisted) {
				return;
			}

			hydrateStoredSession()
				.then((next: StoredSession | null) => {
					setSession(next);
				})
				.catch(() => {
					signOut();
				});
		};

		// eslint-disable-next-line ban/ban -- pageshow is a window lifecycle event, not layout work
		window.addEventListener("pageshow", revalidateAfterRestore);

		return () => {
			window.removeEventListener("pageshow", revalidateAfterRestore);
		};
	}, [signOut]);

	useEffect(() => {
		const syncWhenActive = (): void => {
			if (document.visibilityState === "hidden") {
				return;
			}

			syncSession().catch(() => {
				signOut();
			});
		};

		document.addEventListener("visibilitychange", syncWhenActive);

		return () => {
			document.removeEventListener("visibilitychange", syncWhenActive);
		};
	}, [signOut, syncSession]);

	const value = useMemo(
		(): SessionContextValue => ({
			hasSession: isReady && Boolean(session?.accessToken),
			isReady,
			user: session?.user ?? null,
			signIn,
			signOut,
			setUser,
			refreshUser,
			getAccessToken,
			reactivationNoticeVisible,
			dismissReactivationNotice
		}),
		[
			dismissReactivationNotice,
			getAccessToken,
			isReady,
			reactivationNoticeVisible,
			refreshUser,
			session?.accessToken,
			session?.user,
			setUser,
			signIn,
			signOut
		]
	);

	return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSession = (): SessionContextValue => {
	const context = useContext(SessionContext);

	if (!context) {
		throw new Error("useSession must be used within SessionProvider");
	}

	return context;
};
